import React, { useEffect, useState } from "react";
import ReactDOM from "react-dom";
import {
  useForm,
  Controller,
  SubmitHandler,
} from "react-hook-form";
import { Kiosk } from "../../types/kiosk.type";
import { Campaign } from "../../types/campaign.types";
import {
  KioskConfiguration,
  CreateKioskConfigPayload,
  UpdateKioskConfigPayload,
  DonationAmount,
  CurrencyConfig,
  KioskTextSettings,
  KioskConfigurationReviewRequest,
} from "../../types/kioskConfig.types";
import {
  getKioskConfiguration,
  createKioskConfiguration,
  updateKioskConfiguration,
  submitKioskConfigurationReviewRequest,
  uploadImage,
  getSupportedCurrencies,
} from "../../services/KioskConfigService";
import { showErrorToast, showSuccessToast } from "../../lib/ToastUtility";
import { Button } from "../Button";
import MoneyInput from "./MoneyInput";
import { amountTextToCents } from "../../utils/money";
import "./KioskConfigurationForm.css";

interface KioskConfigurationFormProps {
  kiosk: Kiosk;
  campaigns: Campaign[];
  onSaved: (config: KioskConfiguration) => void;
  restrictToAmountsOnly?: boolean;
  moderatedSubmission?: boolean;
  onReviewSubmissionSuccess?: () => void;
  highlightedTabLabels?: string[];
  pendingReviewRequest?: KioskConfigurationReviewRequest | null;
  onApprovePendingChanges?: (approvedFields?: string[]) => Promise<void> | void;
  onRejectPendingChanges?: (reviewComment: string, rejectedFields?: string[]) => Promise<void> | void;
  approvalLoading?: boolean;
  reloadToken?: string;
}

type FormData = CreateKioskConfigPayload & {
  attractImageFile?: FileList;
  donateImageFile?: FileList;
  thanksImageFile?: FileList;
  partnerLogoFile?: FileList;
  textSettings: KioskTextSettings;
  submissionComment?: string;
};

type FieldRequirement = "required" | "optional";

function FieldLabel({
  text,
  requirement,
  hint,
  defaultValue,
  pendingValue,
  pendingLabel = "Requested update",
  onApprove,
  onReject,
  approving,
}: {
  text: string;
  requirement: FieldRequirement;
  hint?: string;
  defaultValue?: string;
  pendingValue?: string;
  pendingLabel?: string;
  onApprove?: () => void;
  onReject?: () => void;
  approving?: boolean;
}) {
  return (
    <div className="field-label">
      <label>
        {text}
        {requirement === "required" ? <span className="required-asterisk">*</span> : null}
      </label>
      {hint ? <span className="field-hint">{hint}</span> : null}
      {defaultValue || pendingValue ? (
        <div className="field-default-row">
          {defaultValue ? <span className="field-default">Default: {defaultValue}</span> : null}
          {pendingValue ? (
            <span className="field-pending-inline">
              <span className="field-pending-text">{pendingLabel}: {pendingValue}</span>
              {onApprove || onReject ? (
                <span className="field-review-actions" role="group" aria-label={`${text} review actions`}>
                  {onApprove ? (
                    <button
                      type="button"
                      className="field-icon-btn field-approve-btn"
                      onClick={onApprove}
                      disabled={approving}
                      aria-label={`Approve ${text} update`}
                      title="Approve update"
                    >
                      ✓
                    </button>
                  ) : null}
                  {onReject ? (
                    <button
                      type="button"
                      className="field-icon-btn field-reject-btn"
                      onClick={onReject}
                      disabled={approving}
                      aria-label={`Reject ${text} update`}
                      title="Reject update"
                    >
                      ✕
                    </button>
                  ) : null}
                </span>
              ) : null}
            </span>
          ) : null}
        </div>
      ) : null}
    </div>
  );
}

const DEFAULT_DONATION_AMOUNTS: DonationAmount[] = [
  {
    amountCents: 500,
    title: "Feed 2 Children",
    subtitle: "1 nutritious meal",
    tagline: "A small act. A big impact.",
    meals: 2,
    tier: "starter",
  },
  {
    amountCents: 1000,
    title: "Feed 5 Children",
    subtitle: "More meals.",
    tagline: "More hope.",
    meals: 5,
    tier: "popular",
  },
  {
    amountCents: 2000,
    title: "Feed 10 Children",
    subtitle: "Leadership gift",
    tagline: "for bigger impact",
    meals: 10,
    tier: "legacy",
  },
];

const VALID_TIERS: DonationAmount["tier"][] = ["starter", "popular", "legacy"];
type TabKey = "basic" | "attract" | "donate" | "thanks" | "amounts";

const TAB_ORDER: TabKey[] = ["basic", "attract", "donate", "thanks", "amounts"];

const TAB_LABELS: Record<TabKey, string> = {
  basic: "Basic Info",
  attract: "Attract Screen",
  donate: "Donate Screen",
  thanks: "Thanks Screen",
  amounts: "Donation Amounts",
};

const TAB_KEY_BY_LABEL: Record<string, TabKey> = {
  "Basic Info": "basic",
  "Attract Screen": "attract",
  "Donate Screen": "donate",
  "Thanks Screen": "thanks",
  "Donation Amounts": "amounts",
};

const DEFAULT_TEXT_SETTINGS: KioskTextSettings = {
  attractAdminButton: "Admin",
  attractPartnershipLabel: "IN PARTNERSHIP WITH",
  attractLiveImpactEyebrow: "LIVE IMPACT",
  attractLiveImpactCaption: "MEALS PROVIDED",
  attractLiveImpactFootnote: "ACROSS THE TAP2FEED NETWORK",
  attractImpactNodeSuffix: "IMPACT NODE",
  attractChildrenFedLabel: "CHILDREN FED",
  attractRaisedSoFarLabel: "RAISED SO FAR",
  attractTrackLine1: "TRACK",
  attractTrackLine2: "YOUR",
  attractTrackLine3: "IMPACT ↳",
  attractCtaTitle: "TAP ANYWHERE TO GIVE",
  attractCtaSubtitle: "SECURE. CONTACTLESS. 100% IMPACT.",
  attractFooterSecurity: "Your donation is safe and secure.",
  donateTogetherProvidedTitle: "TOGETHER, WE'VE PROVIDED",
  donateMealsInNeedCaption: "MEALS TO CHILDREN IN NEED",
  donateRealMealsLabel: "REAL MEALS",
  donateGlobalImpactLabel: "GLOBAL IMPACT",
  donateHopeRestoredLabel: "HOPE RESTORED",
  donateTodaysGoalLabel: "TODAY'S GOAL",
  donateMealsTodayLabel: "meals today",
  donateChooseImpactTitle: "CHOOSE YOUR IMPACT",
  donateAmountSubtitle: "Every £2 provides one nutritious FOOD bar",
  donateMostPopularLabel: "MOST POPULAR",
  donateOtherAmountLabel: "OTHER AMOUNT",
  donateOtherAmountHint: "ENTER ANOTHER AMOUNT",
  donateSecureRibbon: "Secure • Contactless • Trackable impact",
  donateTapAnywhereText: "Tap anywhere to give",
  donateTrackTitle: "TRACK YOUR IMPACT",
  donateTrackBody: "Scan the QR code to see where your meals go and the lives you're changing.",
  donateBottomRibbonLeft: "Thank you for being part of the change.",
  donateBottomRibbonLeftAccent: "TOGETHER, WE CAN FEED MORE PEOPLE.",
  donateBottomRibbonRight: "Proud to support a better tomorrow.",
  donateTrustSecureTitle: "SECURE & PRIVATE",
  donateTrustSecureBody: "Your donation is safe\nand encrypted",
  donateTrustTransparentTitle: "TRANSPARENT",
  donateTrustTransparentBody: "See where your\nmoney goes",
  donateTrustPartnerTitle: "TRUSTED PARTNER",
  donateTrustPartnerBody: "Working with world-class\nhumanitarian experts",
  donateTrustImpactTitle: "REAL IMPACT",
  donateTrustImpactBody: "Every tap\nfeeds a child",
  thanksSponsorLabel: "Sponsored by",
  thanksYouHelpedFeedPrefix: "You just helped feed",
  thanksPaymentSuccessfulTitle: "Payment Successful",
  thanksPaymentSuccessfulSubtitle: "Your donation has been received.",
  thanksEmailReceiptLabel: "EMAIL ME A RECEIPT",
  thanksSendButton: "Send",
  thanksSendingButton: "Sending…",
  thanksInvalidEmailMessage: "Please enter a valid email address.",
  thanksOptionalEmailHint: "Optional — get a receipt and impact updates.",
  thanksNoThanksReturn: "No thanks — return home",
  thanksSecureFootStrong: "Secure. Contactless. 100% Impact.",
  thanksImpactCardTitle: "YOUR IMPACT",
  thanksChildFedSingle: "CHILD FED",
  thanksChildFedPlural: "CHILDREN FED",
  thanksBecauseGift: "Because of your\ngenerous gift today.",
  thanksThankYouSmall: "Thank you",
  thanksHungerFreeTomorrow: "You are helping build a\nhunger-free tomorrow.",
  thanksUpdatingPrefix: "Updating",
  thanksTrackTitle: "TRACK YOUR IMPACT",
  thanksTrackBody: "Scan to see where your donation goes and the lives you're changing.",
  thanksShareTitle: "SHARE YOUR IMPACT",
  thanksShareBody: "Inspire others to make a difference.",
  thanksFooterStrong: "TOGETHER, WE CAN END HUNGER.",
  thanksFooterSub: "Thank you for being the reason a child eats today.",
  thanksReturnMessage: "Returning to start in 10 seconds",
  thanksGlobalImpactLabel: "FOOD BARS PROVIDED WORLDWIDE",
  thanksImpactMessageTemplate: "YOUR {amount} HAS PROVIDED {meals} FOOD BARS",
  thanksTouchEmailLabel: "TOUCH TO ADD YOUR EMAIL",
  thanksBackHomeLabel: "Return to Start",
  thanksEmailDialogReturnNotice: "This screen will return to the main screen in 2 minutes.",
};

const TEXT_FIELD_GROUPS_BY_TAB: Record<Extract<TabKey, "attract" | "donate" | "thanks">, { title: string; keys: string[] }> = {
  attract: {
    title: "Attract Screen Labels",
    keys: [
      "attractAdminButton",
      "attractPartnershipLabel",
      "attractLiveImpactEyebrow",
      "attractLiveImpactCaption",
      "attractLiveImpactFootnote",
      "attractImpactNodeSuffix",
      "attractChildrenFedLabel",
      "attractRaisedSoFarLabel",
      "attractTrackLine1",
      "attractTrackLine2",
      "attractTrackLine3",
      "attractCtaTitle",
      "attractCtaSubtitle",
      "attractFooterSecurity",
    ],
  },
  donate: {
    title: "Donate Screen Labels",
    keys: [
      "donateTogetherProvidedTitle",
      "donateMealsInNeedCaption",
      "donateRealMealsLabel",
      "donateGlobalImpactLabel",
      "donateHopeRestoredLabel",
      "donateTodaysGoalLabel",
      "donateMealsTodayLabel",
      "donateChooseImpactTitle",
      "donateAmountSubtitle",
      "donateMostPopularLabel",
      "donateOtherAmountLabel",
      "donateOtherAmountHint",
      "donateSecureRibbon",
      "donateTapAnywhereText",
      "donateTrackTitle",
      "donateTrackBody",
      "donateBottomRibbonLeft",
      "donateBottomRibbonLeftAccent",
      "donateBottomRibbonRight",
      "donateTrustSecureTitle",
      "donateTrustSecureBody",
      "donateTrustTransparentTitle",
      "donateTrustTransparentBody",
      "donateTrustPartnerTitle",
      "donateTrustPartnerBody",
      "donateTrustImpactTitle",
      "donateTrustImpactBody",
    ],
  },
  thanks: {
    title: "Thanks Screen Labels",
    keys: [
      "thanksSponsorLabel",
      "thanksYouHelpedFeedPrefix",
      "thanksPaymentSuccessfulTitle",
      "thanksPaymentSuccessfulSubtitle",
      "thanksEmailReceiptLabel",
      "thanksSendButton",
      "thanksSendingButton",
      "thanksInvalidEmailMessage",
      "thanksOptionalEmailHint",
      "thanksNoThanksReturn",
      "thanksSecureFootStrong",
      "thanksImpactCardTitle",
      "thanksChildFedSingle",
      "thanksChildFedPlural",
      "thanksBecauseGift",
      "thanksThankYouSmall",
      "thanksHungerFreeTomorrow",
      "thanksUpdatingPrefix",
      "thanksTrackTitle",
      "thanksTrackBody",
      "thanksShareTitle",
      "thanksShareBody",
      "thanksFooterStrong",
      "thanksFooterSub",
      "thanksReturnMessage",
      "thanksGlobalImpactLabel",
      "thanksImpactMessageTemplate",
      "thanksTouchEmailLabel",
      "thanksBackHomeLabel",
      "thanksEmailDialogReturnNotice",
    ],
  },
};

function hasPathError(source: unknown, path: string): boolean {
  const parts = path.split(".");
  let current: any = source;

  for (const part of parts) {
    if (current == null) {
      return false;
    }
    current = current[part];
  }

  return Boolean(current);
}

function sanitizeOptionalText(value: unknown): string | undefined {
  if (typeof value !== "string") return undefined;
  const trimmed = value.trim();
  return trimmed.length > 0 ? trimmed : undefined;
}

function hasText(value: unknown): boolean {
  return typeof value === "string" && value.trim().length > 0;
}

function isValidOptionalUrl(value: unknown): boolean {
  if (!hasText(value)) {
    return true;
  }

  try {
    new URL(String(value));
    return true;
  } catch {
    return false;
  }
}

function isValidDonationAmount(value: Partial<DonationAmount> | undefined): boolean {
  if (!value) {
    return false;
  }

  return (
    Number.isInteger(value.amountCents) &&
    Number(value.amountCents) >= 1 &&
    hasText(value.title) &&
    String(value.title).trim().length <= 120 &&
    hasText(value.subtitle) &&
    String(value.subtitle).trim().length <= 180 &&
    hasText(value.tagline) &&
    String(value.tagline).trim().length <= 180 &&
    Number.isInteger(value.meals) &&
    Number(value.meals) >= 0 &&
    VALID_TIERS.includes(value.tier as DonationAmount["tier"])
  );
}

function humanizeTextKey(key: string): string {
  const stripped = key.replace(/^(attract|donate|thanks)/, "");
  const spaced = stripped.replace(/([A-Z])/g, " $1").trim();
  return spaced.length > 0 ? spaced : key;
}

function sanitizeDonationAmounts(input: unknown): DonationAmount[] {
  const source = Array.isArray(input) ? input : DEFAULT_DONATION_AMOUNTS;

  return DEFAULT_DONATION_AMOUNTS.map((_, index) => {
    const item = source[index];
    const fallback = DEFAULT_DONATION_AMOUNTS[index] ?? DEFAULT_DONATION_AMOUNTS[0];
    const raw = (item ?? {}) as Partial<DonationAmount>;

    const amountValue = Number(raw.amountCents);
    const amountCents = Number.isFinite(amountValue) ? Math.round(amountValue) : fallback.amountCents;

    const mealsValue = Number(raw.meals);
    const meals = Number.isFinite(mealsValue) ? Math.max(0, Math.round(mealsValue)) : fallback.meals;

    const tier = VALID_TIERS.includes(raw.tier as DonationAmount["tier"])
      ? (raw.tier as DonationAmount["tier"])
      : fallback.tier;

    const title = sanitizeOptionalText(raw.title) ?? fallback.title;
    const subtitle = sanitizeOptionalText(raw.subtitle) ?? fallback.subtitle;
    const tagline = sanitizeOptionalText(raw.tagline) ?? fallback.tagline;

    return {
      amountCents: amountCents > 0 ? amountCents : fallback.amountCents,
      title,
      subtitle,
      tagline,
      meals,
      tier,
    };
  });
}

export function KioskConfigurationForm({
  kiosk,
  campaigns,
  onSaved,
  restrictToAmountsOnly = false,
  moderatedSubmission = false,
  onReviewSubmissionSuccess,
  highlightedTabLabels = [],
  pendingReviewRequest = null,
  onApprovePendingChanges,
  onRejectPendingChanges,
  approvalLoading = false,
  reloadToken,
}: KioskConfigurationFormProps) {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [existingConfig, setExistingConfig] = useState<KioskConfiguration | null>(null);
  const [activeTab, setActiveTab] = useState<TabKey>(restrictToAmountsOnly ? "amounts" : "basic");
  const [visitedTabs, setVisitedTabs] = useState<Record<TabKey, boolean>>({
    basic: true,
    attract: false,
    donate: false,
    thanks: false,
    amounts: false,
  });
  const [uploadingImages, setUploadingImages] = useState(false);
  const [currencyOptions, setCurrencyOptions] = useState<CurrencyConfig[]>([]);
  const [loadingCurrencies, setLoadingCurrencies] = useState(true);
  const [showSubmitDialog, setShowSubmitDialog] = useState(false);
  const [showRejectDialog, setShowRejectDialog] = useState(false);
  const [rejectActionScope, setRejectActionScope] = useState<"request" | "field" | "tab" | "all">("request");
  const [rejectFieldKeys, setRejectFieldKeys] = useState<string[]>([]);
  const [rejectComment, setRejectComment] = useState("");
  const {
    control,
    handleSubmit,
    setValue,
    trigger,
    formState: { errors },
    watch,
  } = useForm<FormData>({
    mode: "onChange",
    reValidateMode: "onChange",
    defaultValues: {
      campaignId: campaigns[0]?.id || 0,
      partnerName: "",
      showPartnerBranding: true,
      attractHeadline: "Make a Difference Today",
      donateHeadline: "Your Kindness Counts",
      thanksHeadline: "Thank You!",
      donateDailyGoal: 500,
      currencyCode: "GBP",
      currencySymbol: "£",
      currencyName: "British Pound",
      donationAmounts: DEFAULT_DONATION_AMOUNTS,
      textSettings: DEFAULT_TEXT_SETTINGS,
      submissionComment: "",
    },
  });

  const formValues = watch();
  const donationAmounts = watch("donationAmounts");
  const selectedCurrencyCode = watch("currencyCode");

  const selectedCurrency = currencyOptions.find((option) => option.code === selectedCurrencyCode);

  const amountFieldPaths = (donationAmounts ?? DEFAULT_DONATION_AMOUNTS).flatMap((_, index) => [
    `donationAmounts.${index}.amountCents`,
    `donationAmounts.${index}.title`,
    `donationAmounts.${index}.subtitle`,
    `donationAmounts.${index}.tagline`,
    `donationAmounts.${index}.meals`,
    `donationAmounts.${index}.tier`,
  ]);

  const attractTextFieldPaths = TEXT_FIELD_GROUPS_BY_TAB.attract.keys.map((key) => `textSettings.${key}`);
  const donateTextFieldPaths = TEXT_FIELD_GROUPS_BY_TAB.donate.keys.map((key) => `textSettings.${key}`);
  const thanksTextFieldPaths = TEXT_FIELD_GROUPS_BY_TAB.thanks.keys.map((key) => `textSettings.${key}`);

  const tabFieldPaths: Record<TabKey, string[]> = {
    basic: [
      "campaignId",
      "partnerName",
      "partnerLogoUrl",
      "currencyCode",
      "currencySymbol",
      "currencyName",
    ],
    attract: ["attractHeadline", "attractDescription", "attractBackgroundImageUrl", "customQrCodeUrl", ...attractTextFieldPaths],
    donate: ["donateHeadline", "donateDescription", "donateDailyGoal", "donateBackgroundImageUrl", ...donateTextFieldPaths],
    thanks: ["thanksHeadline", "thanksDescription", "thanksBackgroundImageUrl", ...thanksTextFieldPaths],
    amounts: ["donationAmounts", ...amountFieldPaths],
  };

  const tabErrorCounts: Record<TabKey, number> = {
    basic: tabFieldPaths.basic.filter((path) => hasPathError(errors, path)).length,
    attract: tabFieldPaths.attract.filter((path) => hasPathError(errors, path)).length,
    donate: tabFieldPaths.donate.filter((path) => hasPathError(errors, path)).length,
    thanks: tabFieldPaths.thanks.filter((path) => hasPathError(errors, path)).length,
    amounts: tabFieldPaths.amounts.filter((path) => hasPathError(errors, path)).length,
  };

  const hasValidTextSettings = (keys: string[]) =>
    keys.every((key) => {
      const value = formValues.textSettings?.[key];
      return typeof value === "string" && value.trim().length <= 500;
    });

  const tabCompletion: Record<TabKey, boolean> = {
    basic:
      Number(formValues.campaignId) > 0 &&
      (!formValues.showPartnerBranding || hasText(formValues.partnerName)) &&
      (!hasText(formValues.partnerLogoUrl) || isValidOptionalUrl(formValues.partnerLogoUrl)) &&
      hasText(formValues.currencyCode) &&
      String(formValues.currencyCode).trim().length <= 10 &&
      hasText(formValues.currencySymbol) &&
      String(formValues.currencySymbol).trim().length <= 10 &&
      hasText(formValues.currencyName) &&
      String(formValues.currencyName).trim().length <= 255 &&
      String(formValues.partnerName).trim().length <= 255,
    attract:
      hasText(formValues.attractHeadline) &&
      String(formValues.attractHeadline).trim().length <= 1000 &&
      (!hasText(formValues.attractDescription) || String(formValues.attractDescription).trim().length <= 5000) &&
      hasText(formValues.attractBackgroundImageUrl) &&
      isValidOptionalUrl(formValues.attractBackgroundImageUrl) &&
      (!hasText(formValues.customQrCodeUrl) || isValidOptionalUrl(formValues.customQrCodeUrl)) &&
      hasValidTextSettings(TEXT_FIELD_GROUPS_BY_TAB.attract.keys),
    donate:
      hasText(formValues.donateHeadline) &&
      String(formValues.donateHeadline).trim().length <= 1000 &&
      (!hasText(formValues.donateDescription) || String(formValues.donateDescription).trim().length <= 5000) &&
      Number.isInteger(Number(formValues.donateDailyGoal)) &&
      Number(formValues.donateDailyGoal) >= 1 &&
      hasText(formValues.donateBackgroundImageUrl) &&
      isValidOptionalUrl(formValues.donateBackgroundImageUrl) &&
      hasValidTextSettings(TEXT_FIELD_GROUPS_BY_TAB.donate.keys),
    thanks:
      hasText(formValues.thanksHeadline) &&
      String(formValues.thanksHeadline).trim().length <= 1000 &&
      (!hasText(formValues.thanksDescription) || String(formValues.thanksDescription).trim().length <= 5000) &&
      hasText(formValues.thanksBackgroundImageUrl) &&
      isValidOptionalUrl(formValues.thanksBackgroundImageUrl) &&
      hasValidTextSettings(TEXT_FIELD_GROUPS_BY_TAB.thanks.keys),
    amounts:
      Array.isArray(formValues.donationAmounts) &&
      formValues.donationAmounts.length === 3 &&
      formValues.donationAmounts.every((amount) => isValidDonationAmount(amount as Partial<DonationAmount>)),
  };

  const tabHasErrors: Record<TabKey, boolean> = {
    basic: tabErrorCounts.basic > 0,
    attract: tabErrorCounts.attract > 0,
    donate: tabErrorCounts.donate > 0,
    thanks: tabErrorCounts.thanks > 0,
    amounts: tabErrorCounts.amounts > 0,
  };

  const tabIsComplete: Record<TabKey, boolean> = {
    basic: tabCompletion.basic && !tabHasErrors.basic && visitedTabs.basic,
    attract: tabCompletion.attract && !tabHasErrors.attract && visitedTabs.attract,
    donate: tabCompletion.donate && !tabHasErrors.donate && visitedTabs.donate,
    thanks: tabCompletion.thanks && !tabHasErrors.thanks && visitedTabs.thanks,
    amounts: tabCompletion.amounts && !tabHasErrors.amounts && visitedTabs.amounts,
  };

  const highlightedTabs = new Set<TabKey>(
    highlightedTabLabels
      .map((label) => TAB_KEY_BY_LABEL[label])
      .filter((tab): tab is TabKey => Boolean(tab)),
  );

  const handleTabChange = (tab: TabKey) => {
    setActiveTab(tab);
    setVisitedTabs((prev) => ({ ...prev, [tab]: true }));
  };

  const reviewMode = Boolean(
    pendingReviewRequest &&
      !moderatedSubmission &&
      (onApprovePendingChanges || onRejectPendingChanges),
  );

  const getValueByPath = (source: unknown, path: string): unknown => {
    const parts = path.split(".");
    let current: any = source;

    for (const part of parts) {
      if (current == null) {
        return undefined;
      }
      current = current[part];
    }

    return current;
  };

  const toDisplayValue = (value: unknown): string => {
    if (value == null || value === "") {
      return "Not set";
    }
    if (typeof value === "string" || typeof value === "number") {
      return String(value);
    }
    if (typeof value === "boolean") {
      return value ? "Enabled" : "Disabled";
    }
    return JSON.stringify(value);
  };

  const fieldPathsByTab: Record<TabKey, string[]> = {
    basic: [
      "campaignId",
      "partnerName",
      "partnerLogoUrl",
      "showPartnerBranding",
      "currencyCode",
      "currencySymbol",
      "currencyName",
    ],
    attract: [
      "attractHeadline",
      "attractHeadlineColor",
      "attractDescription",
      "attractDescriptionColor",
      "attractBackgroundImageUrl",
      "customQrCodeUrl",
      ...TEXT_FIELD_GROUPS_BY_TAB.attract.keys.map((key) => `textSettings.${key}`),
    ],
    donate: [
      "donateHeadline",
      "donateHeadlineColor",
      "donateDescription",
      "donateDescriptionColor",
      "donateBackgroundImageUrl",
      "donateDailyGoal",
      ...TEXT_FIELD_GROUPS_BY_TAB.donate.keys.map((key) => `textSettings.${key}`),
    ],
    thanks: [
      "thanksHeadline",
      "thanksHeadlineColor",
      "thanksDescription",
      "thanksDescriptionColor",
      "thanksBackgroundImageUrl",
      ...TEXT_FIELD_GROUPS_BY_TAB.thanks.keys.map((key) => `textSettings.${key}`),
    ],
    amounts: ["donationAmounts"],
  };

  const getChangedFieldKeys = (tab: TabKey): string[] => {
    if (!pendingReviewRequest) {
      return [];
    }

    const base = pendingReviewRequest.baseConfiguration;
    const proposed = pendingReviewRequest.proposedConfiguration;

    return fieldPathsByTab[tab].filter((path) => {
      const oldValue = getValueByPath(base, path);
      const newValue = getValueByPath(proposed, path);
      return JSON.stringify(oldValue ?? null) !== JSON.stringify(newValue ?? null);
    });
  };

  const changedFieldKeysForActiveTab = getChangedFieldKeys(activeTab);
  const allChangedFieldKeys = TAB_ORDER.flatMap((tab) => getChangedFieldKeys(tab));

  const handleRejectCurrentRequest = async () => {
    const trimmedComment = rejectComment.trim();
    if (!trimmedComment) {
      showErrorToast("Review comment is required when rejecting a request");
      return;
    }

    try {
      const effectiveRejectedFields = rejectActionScope === "request"
        ? undefined
        : rejectFieldKeys.length > 0
          ? rejectFieldKeys
          : undefined;

      await onRejectPendingChanges?.(trimmedComment, effectiveRejectedFields);
      setRejectComment("");
      setRejectActionScope("request");
      setRejectFieldKeys([]);
      setShowRejectDialog(false);
    } catch (error) {
      showErrorToast((error as Error).message || "Failed to reject pending changes");
    }
  };

  const openRejectDialog = (
    scope: "request" | "field" | "tab" | "all" = "request",
    fieldKeys?: string[],
  ) => {
    setRejectActionScope(scope);
    setRejectFieldKeys(Array.isArray(fieldKeys) ? Array.from(new Set(fieldKeys)) : []);
    setRejectComment("");
    setShowRejectDialog(true);
  };

  const getFieldReviewValues = (fieldKey: string): { currentValue?: string; pendingValue?: string } => {
    if (!pendingReviewRequest) {
      return {};
    }

    const oldValue = getValueByPath(pendingReviewRequest.baseConfiguration, fieldKey);
    const newValue = getValueByPath(pendingReviewRequest.proposedConfiguration, fieldKey);

    if (JSON.stringify(oldValue ?? null) === JSON.stringify(newValue ?? null)) {
      return {};
    }

    return {
      currentValue: toDisplayValue(oldValue),
      pendingValue: toDisplayValue(newValue),
    };
  };

  const renderActiveTabContent = () => {
    switch (activeTab) {
      case "basic":
        return (
          <div className="tab-content">
            <section className="form-section">
              <div className="form-section-header">
                <h4>Core Setup</h4>
                <p>Set identity and ownership for this kiosk deployment.</p>
              </div>
              <div className="form-grid two-cols">
                <div className="form-group">
                  <FieldLabel text="Campaign" requirement="required" />
                  <Controller
                    name="campaignId"
                    control={control}
                    rules={{
                      required: "Campaign is required",
                      min: { value: 1, message: "Campaign is required" },
                    }}
                    render={({ field }) => (
                      <select {...field} value={field.value || ""}>
                        <option value="">Select a campaign</option>
                        {campaigns.map((c) => (
                          <option key={c.id} value={c.id}>
                            {c.name}
                          </option>
                        ))}
                      </select>
                    )}
                  />
                  {errors.campaignId && <span className="error">{errors.campaignId.message}</span>}
                </div>
              </div>
            </section>

            <section className="form-section">
              <div className="form-section-header">
                <h4>Partner Branding</h4>
                <p>Set the partner identity shown across this kiosk experience.</p>
              </div>
              <div className="form-grid two-cols">
                <div className="form-group full-width">
                  <label style={{ display: "flex", alignItems: "center", gap: 10, cursor: "pointer" }}>
                    <Controller
                      name="showPartnerBranding"
                      control={control}
                      render={({ field }) => (
                        <input
                          type="checkbox"
                          checked={!!field.value}
                          onChange={(e) => field.onChange(e.target.checked)}
                        />
                      )}
                    />
                    <span style={{ fontWeight: 600 }}>Show partner branding on this kiosk</span>
                  </label>
                  <p style={{ margin: "4px 0 0", fontSize: 13, color: "#6b7280" }}>
                    Uncheck to hide the partner logo and name for kiosks without a venue partner.
                  </p>
                </div>

                {formValues.showPartnerBranding && (
                  <>
                    <div className="form-group">
                      <FieldLabel text="Partner Name" requirement="optional" />
                      <Controller
                        name="partnerName"
                        control={control}
                        rules={{
                          maxLength: {
                            value: 255,
                            message: "Partner name must be shorter than or equal to 255 characters",
                          },
                        }}
                        render={({ field }) => <input type="text" {...field} placeholder="TrackYourImpact" />}
                      />
                      {errors.partnerName && <span className="error">{errors.partnerName.message}</span>}
                    </div>

                    <div className="form-group">
                      <FieldLabel text="Partner Logo URL" requirement="optional" />
                      <Controller
                        name="partnerLogoUrl"
                        control={control}
                        rules={{
                          validate: (value) => isValidOptionalUrl(value) || "Partner logo URL must be a valid URL",
                        }}
                        render={({ field }) => <input type="url" {...field} placeholder="https://..." />}
                      />
                      {errors.partnerLogoUrl && <span className="error">{errors.partnerLogoUrl.message}</span>}
                    </div>

                    <div className="form-group">
                      <FieldLabel
                        text="Upload Partner Logo"
                        requirement="optional"
                        hint="Upload an image to auto-fill the partner logo URL."
                      />
                      <input
                        type="file"
                        accept="image/*"
                        onChange={(e) => handleImageUpload(e, "partnerLogoUrl")}
                        disabled={uploadingImages}
                      />
                    </div>
                  </>
                )}
              </div>
            </section>

            <section className="form-section">
              <div className="form-section-header">
                <h4>Currency</h4>
                <p>Choose the display currency for this kiosk.</p>
              </div>
              <div className="form-grid two-cols" style={{ marginBottom: '20px' }}>
                <div className="form-group">
                  <FieldLabel text="Currency" requirement="required" />
                  <Controller
                    name="currencyCode"
                    control={control}
                    rules={{
                      required: "Currency is required",
                      maxLength: {
                        value: 10,
                        message: "Currency code must be shorter than or equal to 10 characters",
                      },
                    }}
                    render={({ field }) => (
                      <select
                        {...field}
                        value={field.value || ""}
                        disabled={loadingCurrencies}
                        onChange={(event) => {
                          const code = event.target.value;
                          const option = currencyOptions.find((item) => item.code === code);

                          field.onChange(code);
                          setValue("currencySymbol", option?.symbol || "", { shouldValidate: true, shouldDirty: true });
                          setValue("currencyName", option?.name || "", { shouldValidate: true, shouldDirty: true });
                        }}
                      >
                        <option value="">{loadingCurrencies ? "Loading currencies..." : "Select a currency"}</option>
                        {currencyOptions.map((option) => (
                          <option key={option.code} value={option.code}>
                            {option.code} - {option.name}
                          </option>
                        ))}
                      </select>
                    )}
                  />
                  {errors.currencyCode && <span className="error">{errors.currencyCode.message}</span>}
                </div>

                <div className="form-group">
                  <FieldLabel text="Symbol" requirement="required" />
                  <div className="readonly-field">{selectedCurrency?.symbol || formValues.currencySymbol || "-"}</div>
                </div>

                <div className="form-group">
                  <FieldLabel text="Name" requirement="required" />
                  <div className="readonly-field">{selectedCurrency?.name || formValues.currencyName || "-"}</div>
                </div>
              </div>
            </section>
          </div>
        );

      case "attract":
        return (
          <div className="tab-content">
            <section className="form-section">
              <div className="form-section-header">
                <h4>Message Content</h4>
                <p>Primary copy that appears on the attract screen.</p>
              </div>
              <div className="form-grid two-cols">
                <div className="form-group full-width">
                  <FieldLabel
                    text="Headline"
                    requirement="required"
                    defaultValue={getFieldReviewValues("attractHeadline").currentValue || "Make a Difference Today"}
                    pendingValue={getFieldReviewValues("attractHeadline").pendingValue}
                    onApprove={
                      reviewMode
                        ? () => onApprovePendingChanges?.(["attractHeadline"])
                        : undefined
                    }
                    onReject={reviewMode ? () => openRejectDialog("field", ["attractHeadline"]) : undefined}
                    approving={approvalLoading}
                  />
                  <Controller
                    name="attractHeadline"
                    control={control}
                    rules={{
                      required: "Headline is required",
                      maxLength: {
                        value: 1000,
                        message: "Headline must be shorter than or equal to 1000 characters",
                      },
                    }}
                    render={({ field }) => <input type="text" {...field} placeholder="Make a Difference Today" />}
                  />
                  {errors.attractHeadline && <span className="error">{errors.attractHeadline.message}</span>}
                </div>

                <div className="form-group full-width">
                  <FieldLabel
                    text="Description"
                    requirement="optional"
                    defaultValue={getFieldReviewValues("attractDescription").currentValue || "Feed a child in 10 seconds."}
                    pendingValue={getFieldReviewValues("attractDescription").pendingValue}
                    onApprove={
                      reviewMode
                        ? () => onApprovePendingChanges?.(["attractDescription"])
                        : undefined
                    }
                    onReject={reviewMode ? () => openRejectDialog("field", ["attractDescription"]) : undefined}
                    approving={approvalLoading}
                  />
                  <Controller
                    name="attractDescription"
                    control={control}
                    rules={{
                      maxLength: {
                        value: 5000,
                        message: "Description must be shorter than or equal to 5000 characters",
                      },
                    }}
                    render={({ field }) => (
                      <textarea {...field} placeholder="Enter screen description" rows={4} />
                    )}
                  />
                  {errors.attractDescription && <span className="error">{errors.attractDescription.message}</span>}
                </div>

                <div className="form-group">
                  <FieldLabel text="Headline Text Color" requirement="optional" hint="Color of the headline text on the attract screen background." />
                  <Controller
                    name="attractHeadlineColor"
                    control={control}
                    render={({ field }) => (
                      <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                        <input
                          type="color"
                          value={field.value || "#ffffff"}
                          onChange={(e) => field.onChange(e.target.value)}
                          style={{ width: 44, height: 36, padding: 2, cursor: "pointer", border: "1px solid #ccc", borderRadius: 4 }}
                        />
                        <input
                          type="text"
                          value={field.value || ""}
                          onChange={(e) => field.onChange(e.target.value)}
                          placeholder="#ffffff"
                          maxLength={20}
                          style={{ flex: 1 }}
                        />
                      </div>
                    )}
                  />
                </div>

                <div className="form-group">
                  <FieldLabel text="Description Text Color" requirement="optional" hint="Color of the description text on the attract screen background." />
                  <Controller
                    name="attractDescriptionColor"
                    control={control}
                    render={({ field }) => (
                      <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                        <input
                          type="color"
                          value={field.value || "#ffffff"}
                          onChange={(e) => field.onChange(e.target.value)}
                          style={{ width: 44, height: 36, padding: 2, cursor: "pointer", border: "1px solid #ccc", borderRadius: 4 }}
                        />
                        <input
                          type="text"
                          value={field.value || ""}
                          onChange={(e) => field.onChange(e.target.value)}
                          placeholder="#ffffff"
                          maxLength={20}
                          style={{ flex: 1 }}
                        />
                      </div>
                    )}
                  />
                </div>
              </div>
            </section>

            <section className="form-section">
              <div className="form-section-header">
                <h4>Background Media</h4>
                <p>Provide a URL or upload an image.</p>
              </div>
              <div className="form-grid two-cols">
                <div className="form-group">
                  <FieldLabel text="Background Image URL" requirement="required" />
                  <Controller
                    name="attractBackgroundImageUrl"
                    control={control}
                    rules={{
                      validate: (value) => {
                        if (!hasText(value)) {
                          return "Background image URL or uploaded image is required";
                        }

                        return isValidOptionalUrl(value) || "Background image URL must be a valid URL";
                      },
                    }}
                    render={({ field }) => <input type="url" {...field} placeholder="https://..." />}
                  />
                  {errors.attractBackgroundImageUrl && <span className="error">{errors.attractBackgroundImageUrl.message}</span>}
                </div>

                <div className="form-group">
                  <FieldLabel text="Upload Background Image" requirement="required" hint="Provide a background URL or upload an image. Upload updates the saved URL automatically." />
                  <input
                    type="file"
                    accept="image/*"
                    onChange={(e) => handleImageUpload(e, "attractBackgroundImageUrl")}
                    disabled={uploadingImages}
                  />
                </div>
              </div>
            </section>

            <section className="form-section">
              <div className="form-section-header">
                <h4>TV QR Code</h4>
                <p>Upload a custom QR image for the TV screen. Leave it empty to use the secure default QR code.</p>
              </div>
              <div className="form-grid two-cols">
                <div className="form-group">
                  <FieldLabel text="Custom QR Code Image URL" requirement="optional" />
                  <Controller
                    name="customQrCodeUrl"
                    control={control}
                    rules={{
                      validate: (value) => !hasText(value) || isValidOptionalUrl(value) || "QR code image URL must be a valid URL",
                    }}
                    render={({ field }) => <input type="url" {...field} placeholder="https://..." />}
                  />
                  {errors.customQrCodeUrl && <span className="error">{errors.customQrCodeUrl.message}</span>}
                </div>

                <div className="form-group">
                  <FieldLabel text="Upload Custom QR Code" requirement="optional" hint="PNG, JPG, or WebP. Uploading replaces the current custom QR image." />
                  <input
                    type="file"
                    accept="image/png,image/jpeg,image/webp"
                    onChange={(e) => handleImageUpload(e, "customQrCodeUrl")}
                    disabled={uploadingImages}
                  />
                </div>
              </div>
            </section>

            {renderTextSettingsSection("attract")}
          </div>
        );

      case "donate":
        return (
          <div className="tab-content">
            <section className="form-section">
              <div className="form-section-header">
                <h4>Message Content</h4>
                <p>Text displayed while donors choose donation amounts.</p>
              </div>
              <div className="form-grid two-cols">
                <div className="form-group full-width">
                  <FieldLabel
                    text="Headline"
                    requirement="required"
                    defaultValue={getFieldReviewValues("donateHeadline").currentValue || "Tap to feed a child in 10 seconds"}
                    pendingValue={getFieldReviewValues("donateHeadline").pendingValue}
                    onApprove={
                      reviewMode
                        ? () => onApprovePendingChanges?.(["donateHeadline"])
                        : undefined
                    }
                    onReject={reviewMode ? () => openRejectDialog("field", ["donateHeadline"]) : undefined}
                    approving={approvalLoading}
                  />
                  <Controller
                    name="donateHeadline"
                    control={control}
                    rules={{
                      required: "Headline is required",
                      maxLength: {
                        value: 1000,
                        message: "Headline must be shorter than or equal to 1000 characters",
                      },
                    }}
                    render={({ field }) => <input type="text" {...field} placeholder="Your Kindness Counts" />}
                  />
                  {errors.donateHeadline && <span className="error">{errors.donateHeadline.message}</span>}
                </div>

                <div className="form-group full-width">
                  <FieldLabel
                    text="Description"
                    requirement="optional"
                    defaultValue={getFieldReviewValues("donateDescription").currentValue || "Your tap today can change a tomorrow."}
                    pendingValue={getFieldReviewValues("donateDescription").pendingValue}
                    onApprove={
                      reviewMode
                        ? () => onApprovePendingChanges?.(["donateDescription"])
                        : undefined
                    }
                    onReject={reviewMode ? () => openRejectDialog("field", ["donateDescription"]) : undefined}
                    approving={approvalLoading}
                  />
                  <Controller
                    name="donateDescription"
                    control={control}
                    rules={{
                      maxLength: {
                        value: 5000,
                        message: "Description must be shorter than or equal to 5000 characters",
                      },
                    }}
                    render={({ field }) => (
                      <textarea {...field} placeholder="Enter screen description" rows={4} />
                    )}
                  />
                  {errors.donateDescription && <span className="error">{errors.donateDescription.message}</span>}
                </div>

                <div className="form-group">
                  <FieldLabel text="Headline Text Color" requirement="optional" hint="Color of the headline text on the donate screen background." />
                  <Controller
                    name="donateHeadlineColor"
                    control={control}
                    render={({ field }) => (
                      <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                        <input
                          type="color"
                          value={field.value || "#ffffff"}
                          onChange={(e) => field.onChange(e.target.value)}
                          style={{ width: 44, height: 36, padding: 2, cursor: "pointer", border: "1px solid #ccc", borderRadius: 4 }}
                        />
                        <input
                          type="text"
                          value={field.value || ""}
                          onChange={(e) => field.onChange(e.target.value)}
                          placeholder="#ffffff"
                          maxLength={20}
                          style={{ flex: 1 }}
                        />
                      </div>
                    )}
                  />
                </div>

                <div className="form-group">
                  <FieldLabel text="Description Text Color" requirement="optional" hint="Color of the description text on the donate screen background." />
                  <Controller
                    name="donateDescriptionColor"
                    control={control}
                    render={({ field }) => (
                      <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                        <input
                          type="color"
                          value={field.value || "#ffffff"}
                          onChange={(e) => field.onChange(e.target.value)}
                          style={{ width: 44, height: 36, padding: 2, cursor: "pointer", border: "1px solid #ccc", borderRadius: 4 }}
                        />
                        <input
                          type="text"
                          value={field.value || ""}
                          onChange={(e) => field.onChange(e.target.value)}
                          placeholder="#ffffff"
                          maxLength={20}
                          style={{ flex: 1 }}
                        />
                      </div>
                    )}
                  />
                </div>

                <div className="form-group">
                  <FieldLabel text="Daily Goal (meals)" requirement="required" hint="Used for progress indicators on kiosk." />
                  <Controller
                    name="donateDailyGoal"
                    control={control}
                    rules={{
                      required: "Daily goal is required",
                      min: { value: 1, message: "Daily goal must not be less than 1" },
                      validate: (value) =>
                        Number.isInteger(Number(value)) || "Daily goal must be an integer number",
                    }}
                    render={({ field }) => (
                      <input type="number" {...field} placeholder="500" min={1} onChange={(e) => field.onChange(Number(e.target.value))} />
                    )}
                  />
                  {errors.donateDailyGoal && <span className="error">{errors.donateDailyGoal.message}</span>}
                </div>
              </div>
            </section>

            <section className="form-section">
              <div className="form-section-header">
                <h4>Background Media</h4>
                <p>Provide a URL or upload an image.</p>
              </div>
              <div className="form-grid two-cols">
                <div className="form-group">
                  <FieldLabel text="Background Image URL" requirement="required" />
                  <Controller
                    name="donateBackgroundImageUrl"
                    control={control}
                    rules={{
                      validate: (value) => {
                        if (!hasText(value)) {
                          return "Background image URL or uploaded image is required";
                        }

                        return isValidOptionalUrl(value) || "Background image URL must be a valid URL";
                      },
                    }}
                    render={({ field }) => <input type="url" {...field} placeholder="https://..." />}
                  />
                  {errors.donateBackgroundImageUrl && <span className="error">{errors.donateBackgroundImageUrl.message}</span>}
                </div>

                <div className="form-group">
                  <FieldLabel text="Upload Background Image" requirement="required" hint="Provide a background URL or upload an image. Upload updates the saved URL automatically." />
                  <input
                    type="file"
                    accept="image/*"
                    onChange={(e) => handleImageUpload(e, "donateBackgroundImageUrl")}
                    disabled={uploadingImages}
                  />
                </div>
              </div>
            </section>

            {renderTextSettingsSection("donate")}
          </div>
        );

      case "thanks":
        return (
          <div className="tab-content">
            <section className="form-section">
              <div className="form-section-header">
                <h4>Message Content</h4>
                <p>Text displayed after successful donation.</p>
              </div>
              <div className="form-grid two-cols">
                <div className="form-group full-width">
                  <FieldLabel
                    text="Headline"
                    requirement="required"
                    defaultValue={getFieldReviewValues("thanksHeadline").currentValue || "Thank you!"}
                    pendingValue={getFieldReviewValues("thanksHeadline").pendingValue}
                    onApprove={
                      reviewMode
                        ? () => onApprovePendingChanges?.(["thanksHeadline"])
                        : undefined
                    }
                    onReject={reviewMode ? () => openRejectDialog("field", ["thanksHeadline"]) : undefined}
                    approving={approvalLoading}
                  />
                  <Controller
                    name="thanksHeadline"
                    control={control}
                    rules={{
                      required: "Headline is required",
                      maxLength: {
                        value: 1000,
                        message: "Headline must be shorter than or equal to 1000 characters",
                      },
                    }}
                    render={({ field }) => <input type="text" {...field} placeholder="Thank You!" />}
                  />
                  {errors.thanksHeadline && <span className="error">{errors.thanksHeadline.message}</span>}
                </div>

                <div className="form-group full-width">
                  <FieldLabel
                    text="Description"
                    requirement="optional"
                    defaultValue={getFieldReviewValues("thanksDescription").currentValue || "Your kindness is creating real change."}
                    pendingValue={getFieldReviewValues("thanksDescription").pendingValue}
                    onApprove={
                      reviewMode
                        ? () => onApprovePendingChanges?.(["thanksDescription"])
                        : undefined
                    }
                    onReject={reviewMode ? () => openRejectDialog("field", ["thanksDescription"]) : undefined}
                    approving={approvalLoading}
                  />
                  <Controller
                    name="thanksDescription"
                    control={control}
                    rules={{
                      maxLength: {
                        value: 5000,
                        message: "Description must be shorter than or equal to 5000 characters",
                      },
                    }}
                    render={({ field }) => (
                      <textarea {...field} placeholder="Enter screen description" rows={4} />
                    )}
                  />
                  {errors.thanksDescription && <span className="error">{errors.thanksDescription.message}</span>}
                </div>

                <div className="form-group">
                  <FieldLabel text="Headline Text Color" requirement="optional" hint="Color of the headline text on the thanks screen background." />
                  <Controller
                    name="thanksHeadlineColor"
                    control={control}
                    render={({ field }) => (
                      <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                        <input
                          type="color"
                          value={field.value || "#ffffff"}
                          onChange={(e) => field.onChange(e.target.value)}
                          style={{ width: 44, height: 36, padding: 2, cursor: "pointer", border: "1px solid #ccc", borderRadius: 4 }}
                        />
                        <input
                          type="text"
                          value={field.value || ""}
                          onChange={(e) => field.onChange(e.target.value)}
                          placeholder="#ffffff"
                          maxLength={20}
                          style={{ flex: 1 }}
                        />
                      </div>
                    )}
                  />
                </div>

                <div className="form-group">
                  <FieldLabel text="Description Text Color" requirement="optional" hint="Color of the description text on the thanks screen background." />
                  <Controller
                    name="thanksDescriptionColor"
                    control={control}
                    render={({ field }) => (
                      <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                        <input
                          type="color"
                          value={field.value || "#ffffff"}
                          onChange={(e) => field.onChange(e.target.value)}
                          style={{ width: 44, height: 36, padding: 2, cursor: "pointer", border: "1px solid #ccc", borderRadius: 4 }}
                        />
                        <input
                          type="text"
                          value={field.value || ""}
                          onChange={(e) => field.onChange(e.target.value)}
                          placeholder="#ffffff"
                          maxLength={20}
                          style={{ flex: 1 }}
                        />
                      </div>
                    )}
                  />
                </div>
              </div>
            </section>

            <section className="form-section">
              <div className="form-section-header">
                <h4>Background Media</h4>
                <p>Provide a URL or upload an image.</p>
              </div>
              <div className="form-grid two-cols">
                <div className="form-group">
                  <FieldLabel text="Background Image URL" requirement="required" />
                  <Controller
                    name="thanksBackgroundImageUrl"
                    control={control}
                    rules={{
                      validate: (value) => {
                        if (!hasText(value)) {
                          return "Background image URL or uploaded image is required";
                        }

                        return isValidOptionalUrl(value) || "Background image URL must be a valid URL";
                      },
                    }}
                    render={({ field }) => <input type="url" {...field} placeholder="https://..." />}
                  />
                  {errors.thanksBackgroundImageUrl && <span className="error">{errors.thanksBackgroundImageUrl.message}</span>}
                </div>

                <div className="form-group">
                  <FieldLabel text="Upload Background Image" requirement="required" hint="Provide a background URL or upload an image. Upload updates the saved URL automatically." />
                  <input
                    type="file"
                    accept="image/*"
                    onChange={(e) => handleImageUpload(e, "thanksBackgroundImageUrl")}
                    disabled={uploadingImages}
                  />
                </div>
              </div>
            </section>

            {renderTextSettingsSection("thanks")}
          </div>
        );

      case "amounts":
        return (
          <div className="tab-content donation-amounts">
            <section className="form-section">
              <div className="form-section-header">
                <h4>Donation Tiers</h4>
                <p>Define three tier cards shown on the donate screen.</p>
              </div>
            </section>

            {restrictToAmountsOnly && (
              <section className="form-section">
                <div className="form-grid two-cols">
                  <div className="form-group">
                    <FieldLabel text="Daily Goal (meals)" requirement="required" hint="Used for progress indicators on kiosk." />
                    <Controller
                      name="donateDailyGoal"
                      control={control}
                      rules={{
                        required: "Daily goal is required",
                        min: { value: 1, message: "Daily goal must not be less than 1" },
                        validate: (value) =>
                          Number.isInteger(Number(value)) || "Daily goal must be an integer number",
                      }}
                      render={({ field }) => (
                        <input type="number" {...field} placeholder="500" min={1} onChange={(e) => field.onChange(Number(e.target.value))} />
                      )}
                    />
                    {errors.donateDailyGoal && <span className="error">{errors.donateDailyGoal.message}</span>}
                  </div>
                </div>
              </section>
            )}

            {donationAmounts?.map((amount, index) => (
              <div key={index} className="donation-tier-card">
                <div className="tier-card-header">
                  <h5>Tier {index + 1}</h5>
                </div>
                <div className="form-row">
                  <div className="form-group">
                    <FieldLabel text="Tier" requirement="required" />
                    <Controller
                      name={`donationAmounts.${index}.tier`}
                      control={control}
                      rules={{
                        required: "Tier is required",
                        validate: (value) =>
                          VALID_TIERS.includes(value as DonationAmount["tier"]) ||
                          "Tier must be one of the following values: starter, popular, legacy",
                      }}
                      render={({ field }) => (
                        <select {...field}>
                          <option value="starter">Starter</option>
                          <option value="popular">Popular</option>
                          <option value="legacy">Legacy</option>
                        </select>
                      )}
                    />
                    {errors.donationAmounts?.[index]?.tier && (
                      <span className="error">{errors.donationAmounts[index]?.tier?.message}</span>
                    )}
                  </div>

                  <div className="form-group">
                    <FieldLabel
                      text="Amount"
                      requirement="required"
                      hint="The full amount donors give, e.g. 5.00. It is saved in cents automatically."
                    />
                    <Controller
                      name={`donationAmounts.${index}.amountCents`}
                      control={control}
                      rules={{
                        validate: (value) => {
                          const cents = Number(value);
                          if (!Number.isInteger(cents) || cents < 1) {
                            return "Enter an amount of at least 0.01";
                          }
                          return amountTextToCents(String(cents / 100)) !== null || "Amount is too large";
                        },
                      }}
                      render={({ field }) => (
                        <MoneyInput
                          value={Number(field.value) || 0}
                          onChange={field.onChange}
                          currencySymbol={formValues.currencySymbol || undefined}
                          invalid={Boolean(errors.donationAmounts?.[index]?.amountCents)}
                        />
                      )}
                    />
                    {errors.donationAmounts?.[index]?.amountCents && (
                      <span className="error">{errors.donationAmounts[index]?.amountCents?.message}</span>
                    )}
                  </div>

                  <div className="form-group">
                    <FieldLabel text="Meals" requirement="required" hint="Use 0 if this tier does not map to meals." />
                    <Controller
                      name={`donationAmounts.${index}.meals`}
                      control={control}
                      rules={{
                        required: "Meals is required",
                        min: { value: 0, message: "Meals must not be less than 0" },
                        validate: (value) =>
                          Number.isInteger(Number(value)) || "Meals must be an integer number",
                      }}
                      render={({ field }) => (
                        <input
                          type="number"
                          {...field}
                          min={0}
                          onChange={(e) => field.onChange(Number(e.target.value))}
                        />
                      )}
                    />
                    {errors.donationAmounts?.[index]?.meals && (
                      <span className="error">{errors.donationAmounts[index]?.meals?.message}</span>
                    )}
                  </div>
                </div>

                <div className="form-group">
                  <FieldLabel text="Title" requirement="required" defaultValue={DEFAULT_DONATION_AMOUNTS[index]?.title} />
                  <Controller
                    name={`donationAmounts.${index}.title`}
                    control={control}
                    rules={{
                      required: "Title is required",
                      maxLength: {
                        value: 120,
                        message: "Title must be shorter than or equal to 120 characters",
                      },
                    }}
                    render={({ field }) => <input type="text" {...field} />}
                  />
                  {errors.donationAmounts?.[index]?.title && (
                    <span className="error">{errors.donationAmounts[index]?.title?.message}</span>
                  )}
                </div>

                <div className="form-group">
                  <FieldLabel text="Subtitle" requirement="required" defaultValue={DEFAULT_DONATION_AMOUNTS[index]?.subtitle} />
                  <Controller
                    name={`donationAmounts.${index}.subtitle`}
                    control={control}
                    rules={{
                      required: "Subtitle is required",
                      maxLength: {
                        value: 180,
                        message: "Subtitle must be shorter than or equal to 180 characters",
                      },
                    }}
                    render={({ field }) => <input type="text" {...field} />}
                  />
                  {errors.donationAmounts?.[index]?.subtitle && (
                    <span className="error">{errors.donationAmounts[index]?.subtitle?.message}</span>
                  )}
                </div>

                <div className="form-group">
                  <FieldLabel text="Tagline" requirement="required" defaultValue={DEFAULT_DONATION_AMOUNTS[index]?.tagline} />
                  <Controller
                    name={`donationAmounts.${index}.tagline`}
                    control={control}
                    rules={{
                      required: "Tagline is required",
                      maxLength: {
                        value: 180,
                        message: "Tagline must be shorter than or equal to 180 characters",
                      },
                    }}
                    render={({ field }) => <input type="text" {...field} />}
                  />
                  {errors.donationAmounts?.[index]?.tagline && (
                    <span className="error">{errors.donationAmounts[index]?.tagline?.message}</span>
                  )}
                </div>
              </div>
            ))}
          </div>
        );

      default:
        return null;
    }
  };

  const renderTextSettingsSection = (tab: Extract<TabKey, "attract" | "donate" | "thanks">) => {
    const group = TEXT_FIELD_GROUPS_BY_TAB[tab];

    return (
      <section className="form-section">
        <div className="form-section-header">
          <h4>{group.title}</h4>
          <p>These labels are dynamic on kiosk. Leave unchanged to use the default text.</p>
        </div>
        <div className="form-grid two-cols">
          {group.keys.map((key) => (
            <div className="form-group" key={key}>
              {(() => {
                const fieldKey = `textSettings.${key}`;
                const reviewValues = getFieldReviewValues(fieldKey);

                return (
              <FieldLabel
                text={humanizeTextKey(key)}
                requirement="optional"
                defaultValue={reviewValues.currentValue || DEFAULT_TEXT_SETTINGS[key]}
                pendingValue={reviewValues.pendingValue}
                onApprove={
                  reviewMode
                    ? () => onApprovePendingChanges?.([fieldKey])
                    : undefined
                }
                onReject={reviewMode ? () => openRejectDialog("field", [fieldKey]) : undefined}
                approving={approvalLoading}
              />
                );
              })()}
              <Controller
                name={`textSettings.${key}` as const}
                control={control}
                rules={{
                  maxLength: {
                    value: 500,
                    message: "Text must be shorter than or equal to 500 characters",
                  },
                }}
                render={({ field }) => (
                  <textarea
                    {...field}
                    value={field.value || ""}
                    rows={3}
                    placeholder={DEFAULT_TEXT_SETTINGS[key]}
                  />
                )}
              />
              {hasPathError(errors, `textSettings.${key}`) ? (
                <span className="error">Invalid value</span>
              ) : null}
            </div>
          ))}
        </div>
      </section>
    );
  };

  // Load existing configuration if it exists
  useEffect(() => {
    const loadCurrencies = async () => {
      try {
        const currencies = await getSupportedCurrencies();
        setCurrencyOptions(currencies);
      } catch (error) {
        showErrorToast((error as Error).message || "Failed to load supported currencies");
      } finally {
        setLoadingCurrencies(false);
      }
    };

    loadCurrencies();
  }, []);

  useEffect(() => {
    const loadConfig = async () => {
      try {
        const config = await getKioskConfiguration(kiosk.kioskId);
        setExistingConfig(config);

        // Populate form with existing data
        setValue("campaignId", config.campaignId);
        setValue("partnerName", config.partnerName);
        setValue("partnerLogoUrl", config.partnerLogoUrl);
        setValue("showPartnerBranding", config.showPartnerBranding !== false);
        
        setValue("attractHeadline", config.attractHeadline);
        setValue("attractHeadlineColor", config.attractHeadlineColor);
        setValue("attractDescription", config.attractDescription);
        setValue("attractDescriptionColor", config.attractDescriptionColor);
        setValue("attractBackgroundImageUrl", config.attractBackgroundImageUrl);
        setValue("customQrCodeUrl", config.customQrCodeUrl || "");
        
        setValue("donateHeadline", config.donateHeadline);
        setValue("donateHeadlineColor", config.donateHeadlineColor);
        setValue("donateDescription", config.donateDescription);
        setValue("donateDescriptionColor", config.donateDescriptionColor);
        setValue("donateBackgroundImageUrl", config.donateBackgroundImageUrl);
        setValue("donateDailyGoal", config.donateDailyGoal);
        
        setValue("thanksHeadline", config.thanksHeadline);
        setValue("thanksHeadlineColor", config.thanksHeadlineColor);
        setValue("thanksDescription", config.thanksDescription);
        setValue("thanksDescriptionColor", config.thanksDescriptionColor);
        setValue("thanksBackgroundImageUrl", config.thanksBackgroundImageUrl);
        
        setValue("donationAmounts", sanitizeDonationAmounts(config.donationAmounts));
        setValue("currencyCode", config.currencyCode);
        setValue("currencySymbol", config.currencySymbol);
        setValue("currencyName", config.currencyName);
        setValue("textSettings", {
          ...DEFAULT_TEXT_SETTINGS,
          ...(config.textSettings || {}),
        });
      } catch (error) {
        if (error instanceof Error && error.message === "Kiosk configuration not found") {
          // No configuration exists yet, which is fine
          console.log("No existing configuration, will create new one");
        } else {
          showErrorToast((error as Error).message || "Failed to load configuration");
        }
      } finally {
        setLoading(false);
      }
    };

    loadConfig();
  }, [kiosk.kioskId, setValue, reloadToken]);

  const handleImageUpload = async (
    event: React.ChangeEvent<HTMLInputElement>,
    fieldName: string,
  ) => {
    const files = event.target.files;
    if (!files || files.length === 0) return;

    setUploadingImages(true);
    try {
      const url = await uploadImage(files[0]);
      setValue(fieldName as keyof FormData, url);
      showSuccessToast("Image uploaded successfully");
    } catch (error) {
      showErrorToast((error as Error).message || "Failed to upload image");
    } finally {
      setUploadingImages(false);
    }
  };

  const onSubmit: SubmitHandler<FormData> = async (data) => {
    const validationTargets = existingConfig || activeTab === "basic"
      ? tabFieldPaths[activeTab]
      : Array.from(new Set([...tabFieldPaths.basic, ...tabFieldPaths[activeTab]]));

    const isValid = await trigger(validationTargets as any);
    if (!isValid || !tabCompletion[activeTab] || tabHasErrors[activeTab]) {
      showErrorToast(`Please fix validation errors in the ${TAB_LABELS[activeTab]} tab before submitting`);
      return;
    }

    setSaving(true);
    try {
      const normalizedDonationAmounts = sanitizeDonationAmounts(data.donationAmounts);
      const activeTabTextSettingKeys =
        activeTab === "attract"
          ? TEXT_FIELD_GROUPS_BY_TAB.attract.keys
          : activeTab === "donate"
          ? TEXT_FIELD_GROUPS_BY_TAB.donate.keys
          : activeTab === "thanks"
          ? TEXT_FIELD_GROUPS_BY_TAB.thanks.keys
          : [];

      const normalizedTextSettings = activeTabTextSettingKeys.length
        ? activeTabTextSettingKeys.reduce<KioskTextSettings>((acc, key) => {
            const rawValue = data.textSettings?.[key];
            if (typeof rawValue !== "string") {
              return acc;
            }

            const trimmed = rawValue.trim();
            if (!trimmed) {
              return acc;
            }

            if (trimmed !== DEFAULT_TEXT_SETTINGS[key]) {
              acc[key] = trimmed;
            }

            return acc;
          }, {})
        : undefined;

      const payload: CreateKioskConfigPayload | UpdateKioskConfigPayload = {
        campaignId: Number(data.campaignId),
        partnerName: sanitizeOptionalText(data.partnerName),
        partnerLogoUrl: sanitizeOptionalText(data.partnerLogoUrl),
        showPartnerBranding: data.showPartnerBranding !== false,
        attractHeadline: sanitizeOptionalText(data.attractHeadline),
        attractHeadlineColor: sanitizeOptionalText(data.attractHeadlineColor),
        attractDescription: sanitizeOptionalText(data.attractDescription),
        attractDescriptionColor: sanitizeOptionalText(data.attractDescriptionColor),
        attractBackgroundImageUrl: sanitizeOptionalText(data.attractBackgroundImageUrl),
        customQrCodeUrl: sanitizeOptionalText(data.customQrCodeUrl),
        donateHeadline: sanitizeOptionalText(data.donateHeadline),
        donateHeadlineColor: sanitizeOptionalText(data.donateHeadlineColor),
        donateDescription: sanitizeOptionalText(data.donateDescription),
        donateDescriptionColor: sanitizeOptionalText(data.donateDescriptionColor),
        donateBackgroundImageUrl: sanitizeOptionalText(data.donateBackgroundImageUrl),
        donateDailyGoal: Number(data.donateDailyGoal),
        thanksHeadline: sanitizeOptionalText(data.thanksHeadline),
        thanksHeadlineColor: sanitizeOptionalText(data.thanksHeadlineColor),
        thanksDescription: sanitizeOptionalText(data.thanksDescription),
        thanksDescriptionColor: sanitizeOptionalText(data.thanksDescriptionColor),
        thanksBackgroundImageUrl: sanitizeOptionalText(data.thanksBackgroundImageUrl),
        donationAmounts: normalizedDonationAmounts,
        currencyCode: sanitizeOptionalText(data.currencyCode),
        currencySymbol: sanitizeOptionalText(data.currencySymbol),
        currencyName: sanitizeOptionalText(data.currencyName),
        textSettings: normalizedTextSettings,
      };

      if (moderatedSubmission) {
        await submitKioskConfigurationReviewRequest(kiosk.kioskId, {
          ...(payload as UpdateKioskConfigPayload),
          submissionComment: sanitizeOptionalText(data.submissionComment),
        });

        setShowSubmitDialog(false);
        showSuccessToast("Changes submitted for admin review");
        onReviewSubmissionSuccess?.();
      } else {
        let result: KioskConfiguration;
        if (existingConfig) {
          result = await updateKioskConfiguration(kiosk.kioskId, payload);
          showSuccessToast("Configuration updated successfully");
        } else {
          result = await createKioskConfiguration(
            kiosk.kioskId,
            payload as CreateKioskConfigPayload,
          );
          showSuccessToast("Configuration created successfully");
        }

        setExistingConfig(result);
        onSaved(result);
      }
    } catch (error) {
      showErrorToast((error as Error).message || "Failed to save configuration");
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return <div className="kiosk-config-form loading">Loading configuration...</div>;
  }

  const rejectDialogTitle =
    rejectActionScope === "field"
      ? "Reject Field Change"
      : rejectActionScope === "tab"
      ? "Reject Tab Changes"
      : rejectActionScope === "all"
        ? "Reject All Changes"
        : "Reject Request";

  const rejectDialog = showRejectDialog
    ? ReactDOM.createPortal(
        <div className="kiosk-config-submit-overlay" role="dialog" aria-modal="true" aria-label="Reject request reason">
          <div className="kiosk-config-submit-modal">
            <div className="kiosk-config-submit-header">
              <div>
                <h3>{rejectDialogTitle}</h3>
                <p>Provide a clear reason for rejecting this kiosk update.</p>
              </div>
              <button
                type="button"
                className="kiosk-config-submit-close"
                onClick={() => {
                  setRejectActionScope("request");
                  setRejectFieldKeys([]);
                  setShowRejectDialog(false);
                }}
                aria-label="Close reject dialog"
              >
                ✕
              </button>
            </div>

            <div className="kiosk-config-submit-body">
              <div className="form-group full-width">
                <label htmlFor="reject-request-reason">Review comment</label>
                <textarea
                  id="reject-request-reason"
                  value={rejectComment}
                  onChange={(event) => setRejectComment(event.target.value)}
                  rows={5}
                  placeholder="Enter rejection reason"
                  disabled={approvalLoading}
                />
              </div>
            </div>

            <div className="kiosk-config-submit-footer">
              <Button
                type="button"
                variant="secondary"
                onClick={() => {
                  setRejectActionScope("request");
                  setRejectFieldKeys([]);
                  setShowRejectDialog(false);
                }}
                disabled={approvalLoading}
              >
                Cancel
              </Button>
              <Button
                type="button"
                onClick={handleRejectCurrentRequest}
                disabled={approvalLoading || rejectComment.trim().length === 0}
              >
                {rejectActionScope === "field"
                  ? "Reject Field Change"
                  : rejectActionScope === "tab"
                  ? "Reject Tab Changes"
                  : rejectActionScope === "all"
                    ? "Reject All Changes"
                    : "Reject Request"}
              </Button>
            </div>
          </div>
        </div>,
        document.body,
      )
    : null;

  const submitDialog = moderatedSubmission && showSubmitDialog
    ? ReactDOM.createPortal(
        <div className="kiosk-config-submit-overlay" role="dialog" aria-modal="true" aria-label="Submit for Approval">
          <div className="kiosk-config-submit-modal">
            <div className="kiosk-config-submit-header">
              <div>
                <h3>Submit for Approval</h3>
                <p>Share an optional note with admins before sending this update for review.</p>
              </div>
              <button
                type="button"
                className="kiosk-config-submit-close"
                onClick={() => setShowSubmitDialog(false)}
                aria-label="Close submit dialog"
              >
                ✕
              </button>
            </div>

            <div className="kiosk-config-submit-body">
              <div className="form-group full-width">
                <FieldLabel text="Submission Note (Optional)" requirement="optional" />
                <Controller
                  name="submissionComment"
                  control={control}
                  rules={{
                    maxLength: {
                      value: 1000,
                      message: "Submission note must be shorter than or equal to 1000 characters",
                    },
                  }}
                  render={({ field }) => (
                    <textarea
                      {...field}
                      rows={4}
                      placeholder="Optional note to admin reviewer"
                    />
                  )}
                />
                {errors.submissionComment && <span className="error">{errors.submissionComment.message}</span>}
              </div>
            </div>

            <div className="kiosk-config-submit-footer">
              <Button
                type="button"
                variant="secondary"
                onClick={() => setShowSubmitDialog(false)}
                disabled={saving}
              >
                Cancel
              </Button>
              <Button
                type="button"
                onClick={handleSubmit(onSubmit)}
                disabled={saving || uploadingImages}
              >
                {saving ? "Submitting..." : "Submit for Approval"}
              </Button>
            </div>
          </div>
        </div>,
        document.body,
      )
    : null;

  return (
    <div className="kiosk-config-form">
      <div className="kiosk-config-tabs">
        {(restrictToAmountsOnly ? (["amounts"] as TabKey[]) : TAB_ORDER).map((tab) => (
          <button
            key={tab}
            type="button"
            className={`tab-button ${activeTab === tab ? "active" : ""} ${tabIsComplete[tab] ? "completed" : "incomplete"} ${highlightedTabs.has(tab) ? "review-updated" : ""}`}
            onClick={() => handleTabChange(tab)}
          >
            <span className="tab-button-content">
              <span>{TAB_LABELS[tab]}</span>
              {tabErrorCounts[tab] > 0 ? <span className="tab-error-count">{tabErrorCounts[tab]}</span> : null}
            </span>
          </button>
        ))}
      </div>

      <form onSubmit={handleSubmit(onSubmit)} className="kiosk-config-content">
        {tabErrorCounts[activeTab] > 0 ? (
          <div className="tab-validation-summary has-errors">
            {tabErrorCounts[activeTab]} validation issue{tabErrorCounts[activeTab] === 1 ? "" : "s"} in {TAB_LABELS[activeTab]}.
          </div>
        ) : null}

        <div className="tab-body-scroll" key={activeTab}>
          {renderActiveTabContent()}
        </div>

        <div className="form-actions">
          {reviewMode ? (
            <div className="review-inline-actions">
              <div className="review-reject-comment">
                <label htmlFor="review-reject-comment">Review comment (required for reject)</label>
                <textarea
                  id="review-reject-comment"
                  value={rejectComment}
                  onChange={(event) => setRejectComment(event.target.value)}
                  placeholder="Enter rejection reason"
                  rows={2}
                  disabled={approvalLoading}
                />
              </div>
              <div className="review-inline-buttons">
                <Button
                  type="button"
                  variant="secondary"
                  disabled={approvalLoading || changedFieldKeysForActiveTab.length === 0}
                  onClick={() => onApprovePendingChanges?.(changedFieldKeysForActiveTab)}
                >
                  Approve All Tab Changes
                </Button>
                <Button
                  type="button"
                  disabled={approvalLoading || allChangedFieldKeys.length === 0}
                  onClick={() => onApprovePendingChanges?.(allChangedFieldKeys)}
                >
                  Approve All Changes
                </Button>
                <Button
                  type="button"
                  variant="secondary"
                  className="!bg-red-600 !text-white !border-red-600 hover:!bg-red-700"
                  disabled={approvalLoading || changedFieldKeysForActiveTab.length === 0 || !onRejectPendingChanges}
                  onClick={() => openRejectDialog("tab", changedFieldKeysForActiveTab)}
                >
                  Reject Tab Changes
                </Button>
                <Button
                  type="button"
                  variant="secondary"
                  className="!bg-red-600 !text-white !border-red-600 hover:!bg-red-700"
                  disabled={approvalLoading || allChangedFieldKeys.length === 0 || !onRejectPendingChanges}
                  onClick={() => openRejectDialog("all", allChangedFieldKeys)}
                >
                  Reject All Changes
                </Button>
              </div>
            </div>
          ) : moderatedSubmission ? (
            <Button
              type="button"
              onClick={() => setShowSubmitDialog(true)}
              disabled={saving || uploadingImages}
            >
              Submit for Approval
            </Button>
          ) : (
            <Button type="submit" disabled={saving || uploadingImages}>
              {saving
                ? "Saving..."
                : existingConfig
                ? "Update Configuration"
                : "Create Configuration"}
            </Button>
          )}
        </div>
      </form>
      {rejectDialog}
      {submitDialog}
    </div>
  );
}
